import Splide from '@splidejs/splide';

// Array to store all the fetch promises
const ajaxCalls = [];

/**
 * Initializes the process of fetching set items' data.
 * It reads product IDs from a data attribute, fetches their details,
 * and then initializes the Splide slider once all data is loaded.
 */
export function getSetItems() {
  console.log('Starting getSetItems...');
  // Clear previous AJAX calls if this function can be called multiple times
  ajaxCalls.length = 0;

  const naehrwertDiv = document.querySelector('.item-naehrwert-set');
  if (!naehrwertDiv) {
    console.error('Element with class "item-naehrwert-set" not found.');
    return;
  }
  const zutatenDiv = document.querySelector('.item-zutaten-set');
  if (!zutatenDiv) {
    console.error('Element with class "item-zutaten-set" not found.');
    return;
  }
  // Clear existing content before adding new items
  const imagesContainer = document.querySelector('.item-naehrwert-set-images-data');
  if (imagesContainer) {
    imagesContainer.innerHTML = '';
  }
  const imagesZutatenContainer = document.querySelector('.item-zutaten-set-images-data');
  if (imagesZutatenContainer) {
    imagesZutatenContainer.innerHTML = '';
  }
  const tablesContainerZutaten = document.querySelector('.item-zutaten-set-tables');
  if (tablesContainerZutaten) {
    tablesContainerZutaten.innerHTML = '';
  }

  const naehrwertIds = naehrwertDiv.getAttribute('data-setnaehrwert');
  if (naehrwertIds) {
    const naehrwertIdsSplit = naehrwertIds.split(',').map(Number);
    console.log('Product IDs to fetch:', naehrwertIdsSplit);

    // Iterate over each product ID and initiate data fetching
    naehrwertIdsSplit.forEach((productId) => {
      getSetItemsData(productId);
    });

    // After all fetch promises have been added to ajaxCalls,
    // wait for all of them to resolve before initializing Splide.
    Promise.all(ajaxCalls)
      .then(() => {
        console.log('All AJAX requests completed. Initializing Splide.');
        console.log('Number of image items:', document.querySelectorAll('.item-naehrwert-set-image-item').length);

        // Add 'active' class to the first image item
        const firstImageItem = document.querySelector('.item-naehrwert-set-image-item');
        if (firstImageItem) {
          firstImageItem.classList.add('active');
        }

        // Add 'active' class to the first table data item
        const firstTableDataItem = document.querySelector('.item-naehrwert-set-table-data');
        if (firstTableDataItem) {
          firstTableDataItem.classList.add('active');
        }

        const firstZutatenImageItem = document.querySelector('.item-zutaten-image-item');
        if (firstZutatenImageItem) {
          firstZutatenImageItem.classList.add('active');
        }

        // Add 'active' class to the first table data item
        const firstZutatenItem = document.querySelector('.item-zutatenliste-data');
        if (firstZutatenItem) {
          firstZutatenItem.classList.add('active');
        }

        // Add click event listeners to image items
        addClickListenersToImageItems();
        addClickListenersToZutatenImageItems();
        initSplideSetItems();
      })
      .catch((error) => {
        console.error('One or more AJAX requests failed:', error);
      });
  } else {
    console.warn('No "data-setnaehrwert" attribute found on ".item-naehrwert-set".');
  }
}

/**
 * Fetches product data and specified metafields for a given product ID.
 * The fetch promise is added to the `ajaxCalls` array.
 * @param {number} productId - The ID of the product to fetch.
 */
function getSetItemsData(productId) {
  const metafields = [
    'Energie_1',
    'Energie_2',
    'Energie_3',
    'Energie_4',
    'Fett',
    'Fett_1',
    'Fett_2',
    'davon_gesaettigte_Fettsaeuren_1',
    'davon_gesaettigte_Fettsaeuren_2',
    'Kohlenhydrate',
    'Kohlenhydrate_1',
    'Kohlenhydrate_2',
    'davon_Zucker',
    'davon_Zucker_1',
    'davon_Zucker_2',
    'Salz',
    'Salz_2',
    'Salz_3',
    'Zutatenliste',
    'Spurentext',
  ];
  const dynamicMetafieldsQuery = generateMetafieldsQueryPart(metafields);

  // Store the AJAX call as a Promise
  const fetchPromise = fetch('https://d4e036-0b.myshopify.com/api/2024-07/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': 'c2e8ec54f37aa76f18183a939f2dd418',
    },
    body: JSON.stringify({
      query: `query GetProductByID {
                product(id: "gid://shopify/Product/${productId}") {
                    id
                    title
                    featuredImage { url altText width height }
                    variants(first: 1) {
                        edges { node { sku ${dynamicMetafieldsQuery} } }
                    }
                }
            }`,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP-Fehler! Status: ${response.status} for product ID: ${productId}`);
      }
      return response.json();
    })
    .then((data) => {
      console.log('Product Data received for ID:', productId, data);
      // Check if product data exists before proceeding
      if (
        !data.data ||
        !data.data.product ||
        !data.data.product.variants ||
        !data.data.product.variants.edges ||
        data.data.product.variants.edges.length === 0
      ) {
        console.warn(`No product data found for ID: ${productId}. Skipping rendering.`);
        return; // Skip if no valid product data
      }
      const productNode = data.data.product.variants.edges[0].node;
      generateSetImagesElement(
        data.data.product.title,
        data.data.product.featuredImage?.url || 'https://placehold.co/200x200/cccccc/333333?text=No+Image',
        productNode.sku
      );
      defineNaehwerte(productNode);
      generateSetZutatenImagesElement(
        data.data.product.title,
        data.data.product.featuredImage?.url || 'https://placehold.co/200x200/cccccc/333333?text=No+Image',
        productNode.sku
      );
      defineZutaten(productNode);
    })
    .catch((error) => {
      console.error(`Error fetching product or metafield data for ID ${productId}:`, error);
      // Re-throw the error so Promise.all can catch it
      throw error;
    });

  ajaxCalls.push(fetchPromise); // Add the promise to the array
}

/**
 * Generates the GraphQL query part for dynamic metafields.
 * @param {string[]} metafieldKeys - An array of metafield keys.
 * @returns {string} The formatted GraphQL query string for metafields.
 */
function generateMetafieldsQueryPart(metafieldKeys) {
  return metafieldKeys
    .map(
      (key) => `
        metafield_${key}: metafield(namespace: "${key}", key: "${key}") {
            value
            type
        }
    `
    )
    .join('');
}

/**
 * Generates and appends an image element for the set item.
 * @param {string} title - The title of the product.
 * @param {string} image - The URL of the featured image.
 * @param {string} sku - The SKU of the product variant.
 */
function generateSetImagesElement(title, image, sku) {
  const imagesContainer = document.querySelector('.item-naehrwert-set-images-data');
  if (!imagesContainer) {
    console.error('Container for set images (.item-naehrwert-set-images-data) not found.');
    return;
  }

  const li = document.createElement('li');
  li.className = 'splide__slide';
  const div = document.createElement('div');
  div.className = 'item-naehrwert-set-image-item';
  div.setAttribute('data-sku', sku);
  div.innerHTML = `<img src="${image}" loading="lazy" alt="${title}" /><span>${title}</span>`;
  li.appendChild(div);
  imagesContainer.appendChild(li);
}
function generateSetZutatenImagesElement(title, image, sku) {
  const imagesContainer = document.querySelector('.item-zutaten-set-images-data');
  if (!imagesContainer) {
    console.error('Container for set images (.item-zutaten-set-images-data) not found.');
    return;
  }

  const li = document.createElement('li');
  li.className = 'splide__slide';
  const div = document.createElement('div');
  div.className = 'item-zutaten-image-item';
  div.setAttribute('data-sku', sku);
  div.innerHTML = `<img src="${image}" loading="lazy" alt="${title}" /><span>${title}</span>`;
  li.appendChild(div);
  imagesContainer.appendChild(li);
}
/**
 * Extracts nutrition values from the product node and generates the nutrition table.
 * @param {object} productNode - The product variant node containing metafields.
 */
function defineNaehwerte(productNode) {
  let energie_kj = (productNode.metafield_Energie_1?.value ?? productNode.metafield_Energie_3?.value) || '';
  let energie_kcal = (productNode.metafield_Energie_2?.value ?? productNode.metafield_Energie_4?.value) || '';
  let fett =
    (productNode.metafield_Fett?.value ?? productNode.metafield_Fett_1?.value ?? productNode.metafield_Fett_2?.value) ||
    '';
  let fettsaeuren =
    (productNode.metafield_davon_gesaettigte_Fettsaeuren_1?.value ??
      productNode.metafield_davon_gesaettigte_Fettsaeuren_2?.value) ||
    '';
  let kohlenhydrate =
    (productNode.metafield_Kohlenhydrate?.value ??
      productNode.metafield_Kohlenhydrate_1?.value ??
      productNode.metafield_Kohlenhydrate_2?.value) ||
    '';
  let zucker =
    (productNode.metafield_davon_Zucker?.value ??
      productNode.metafield_davon_Zucker_1?.value ??
      productNode.metafield_davon_Zucker_2?.value) ||
    '';
  let salz =
    (productNode.metafield_Salz?.value ?? productNode.metafield_Salz_2?.value ?? productNode.metafield_Salz_3?.value) ||
    '';

  const tablesContainer = document.querySelector('.item-naehrwert-set-tables');
  if (!tablesContainer) {
    console.error('Container for nutrition tables (.item-naehrwert-set-tables) not found.');
    return;
  }

  tablesContainer.appendChild(
    generateSetNaehwerteElement(
      productNode.sku,
      energie_kj,
      energie_kcal,
      fett,
      fettsaeuren,
      kohlenhydrate,
      zucker,
      salz
    )
  );
}
function defineZutaten(productNode) {
  let zutatenListe = productNode.metafield_Zutatenliste?.value || '';
  let allergene = productNode.metafield_Spurentext?.value || '';

  const tablesContainer = document.querySelector('.item-zutaten-set-tables');
  if (!tablesContainer) {
    console.error('Container for nutrition tables (.item-zutaten-set-tables) not found.');
    return;
  }

  tablesContainer.appendChild(generateZutatenElement(productNode.sku, zutatenListe, allergene));
}
/**
 * Generates a nutrition table element for a given product SKU and its nutrition values.
 * @param {string} sku - The SKU of the product.
 * @param {string} energie_kj - Energy in kJ.
 * @param {string} energie_kcal - Energy in kcal.
 * @param {string} fett - Fat content.
 * @param {string} fettsaeuren - Saturated fat content.
 * @param {string} kohlenhydrate - Carbohydrate content.
 * @param {string} zucker - Sugar content.
 * @param {string} salz - Salt content.
 * @returns {HTMLDivElement} The generated div element containing the nutrition table.
 */
function generateSetNaehwerteElement(sku, energie_kj, energie_kcal, fett, fettsaeuren, kohlenhydrate, zucker, salz) {
  const div = document.createElement('div');
  div.className = 'item-naehrwert-set-table-data';
  div.setAttribute('data-sku', sku);

  const table = document.createElement('table');
  table.className = 'table-item-naehrwert';

  const tbody = document.createElement('tbody');

  // Add rows only if values are present and not empty
  addRow(tbody, 'Brennwert(kj/kcal)', energie_kj && energie_kcal ? `${energie_kj} kj / ${energie_kcal} kcal` : null);
  addRow(tbody, 'Fett', fett, 'g');
  addRow(tbody, 'davon gesättigte Fettsäuren', fettsaeuren, 'g');
  addRow(tbody, 'Kohlenhydrate', kohlenhydrate, 'g');
  addRow(tbody, 'davon Zucker', zucker, 'g');
  addRow(tbody, 'Eiweiß', '', 'g'); // Placeholder for Eiweiß, as no metafield was provided for it.
  addRow(tbody, 'Salz', salz, 'g');

  table.appendChild(tbody);
  div.appendChild(table);

  return div;
}
function generateZutatenElement(sku, zutatenListe, allergene) {
  const div = document.createElement('div');
  div.className = 'item-zutatenliste-data';
  div.setAttribute('data-sku', sku);

  const pZutaten = document.createElement('p');
  pZutaten.className = 'title-zutaten';
  pZutaten.textContent = 'Zutatenliste';

  const divZutaten = document.createElement('div');
  divZutaten.className = 'title-zutaten';
  divZutaten.insertAdjacentHTML('beforeend', zutatenListe);

  const pAllergene = document.createElement('p');
  pAllergene.className = 'title-zutaten';
  pAllergene.textContent = 'Allergene';

  const divAllergene = document.createElement('div');
  divAllergene.className = 'title-zutaten';
  divAllergene.insertAdjacentHTML('beforeend', allergene);
  if (allergene != '') {
  }

  div.appendChild(pZutaten);
  div.appendChild(divZutaten);
  div.appendChild(pAllergene);
  div.appendChild(divAllergene);

  return div;
}
/**
 * Adds a row to a table body if the value is not null or empty.
 * @param {HTMLTableSectionElement} tbody - The tbody element to append the row to.
 * @param {string} label - The label for the table row.
 * @param {string|null} value - The value to display.
 * @param {string} [unit=''] - The unit for the value (e.g., 'g').
 */
function addRow(tbody, label, value, unit = '') {
  if (value !== null && value !== '') {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td>${label}</td><td>${value} ${unit}</td>`;
    tbody.appendChild(tr);
  }
}

/**
 * Initializes the Splide slider for the set items.
 */
function initSplideSetItems() {
  console.log('Initializing Splide...');
  const splideElement = document.querySelector('.splide-item-naehrwert-set-images');
  if (splideElement) {
    new Splide(splideElement, {
      fixedWidth: '200px',
      drag: 'free',
      gap: '10px',
      mediaQuery: 'min', // Apply settings from min-width breakpoints
      pagination: false,
      arrows: true,
    }).mount();
  } else {
    console.warn('Splide element (.splide-item-naehrwert-set-images) not found. Splide not initialized.');
  }
  const splideElementZutaten = document.querySelector('.splide-item-zutaten-set-images');
  if (splideElementZutaten) {
    new Splide(splideElementZutaten, {
      fixedWidth: '200px',
      drag: 'free',
      gap: '10px',
      mediaQuery: 'min', // Apply settings from min-width breakpoints
      pagination: false,
      arrows: true,
    }).mount();
  } else {
    console.warn('Splide element (.splide-item-zutaten-set-imagess) not found. Splide not initialized.');
  }
}

/**
 * Adds click event listeners to all image items to control active states
 * for both image items and their corresponding nutrition tables.
 */
function addClickListenersToImageItems() {
  const imageItems = document.querySelectorAll('.item-naehrwert-set-image-item');
  const tableItems = document.querySelectorAll('.item-naehrwert-set-table-data');

  imageItems.forEach((item) => {
    item.addEventListener('click', () => {
      // Remove 'active' from all image items
      imageItems.forEach((img) => img.classList.remove('active'));
      // Add 'active' to the clicked image item
      item.classList.add('active');

      // Get the SKU from the clicked image item
      const sku = item.getAttribute('data-sku');

      // Remove 'active' from all table items
      tableItems.forEach((table) => table.classList.remove('active'));
      // Add 'active' to the table item with the matching SKU
      const targetTable = document.querySelector(`.item-naehrwert-set-table-data[data-sku="${sku}"]`);
      if (targetTable) {
        targetTable.classList.add('active');
      }
    });
  });
}
function addClickListenersToZutatenImageItems() {
  const imageItems = document.querySelectorAll('.item-zutaten-image-item');
  const tableItems = document.querySelectorAll('.item-zutatenliste-data');

  imageItems.forEach((item) => {
    item.addEventListener('click', () => {
      // Remove 'active' from all image items
      imageItems.forEach((img) => img.classList.remove('active'));
      // Add 'active' to the clicked image item
      item.classList.add('active');

      // Get the SKU from the clicked image item
      const sku = item.getAttribute('data-sku');

      // Remove 'active' from all table items
      tableItems.forEach((table) => table.classList.remove('active'));
      // Add 'active' to the table item with the matching SKU
      const targetTable = document.querySelector(`.item-zutatenliste-data[data-sku="${sku}"]`);
      if (targetTable) {
        targetTable.classList.add('active');
      }
    });
  });
}
